<template>
  <div class="game-prizes-editor">
    <label v-if="configs.label" class="block mb-2">{{ t(configs.label) }}</label>

    <!-- Table header -->
    <div v-if="prizes.length > 0" class="prizes-header">
      <div class="header-drag" />
      <div class="header-label">{{ t('templateBuilder.games.prizeLabel') }}</div>
      <div class="header-image">{{ t('templateBuilder.games.image') }}</div>
      <div class="header-probability">{{ t('templateBuilder.games.probability') }}</div>
      <div v-if="showColorPicker" class="header-color">{{ t('templateBuilder.games.color') }}</div>
      <div class="header-actions" />
    </div>

    <!-- Prize rows -->
    <div class="prizes-list">
      <div
        v-for="(prize, index) in prizes"
        :key="index"
        class="prize-row"
      >
        <div class="row-drag">
          <i class="fas fa-grip-vertical drag-handle" />
        </div>
        <div class="row-label">
          <InputText
            v-model="prize.label"
            class="w-full p-inputtext-sm"
            @update:model-value="onPrizeChange"
          />
        </div>
        <div class="row-image">
          <div class="image-picker-wrapper">
            <Button
              v-if="!prize.image"
              class="image-button image-button-empty"
              icon="far fa-image"
              @click="openImagePicker(index)"
            />
            <div
              v-else
              class="image-thumbnail-wrapper"
              @click="openImagePicker(index)"
            >
              <img :src="prize.image" class="image-thumbnail" />
              <Button
                class="image-remove-btn"
                icon="far fa-times"
                @click.stop="removeImage(index)"
              />
            </div>
          </div>
        </div>
        <div class="row-probability">
          <InputNumber
            v-model="prize.probability"
            :min="0"
            :max="100"
            suffix="%"
            class="w-full p-inputtext-sm"
            @update:model-value="(val) => onProbabilityChange(index, val)"
          />
        </div>
        <div v-if="showColorPicker" class="row-color">
          <div class="color-picker-wrapper">
            <Button
              class="color-button"
              :style="{ 'background-color': prize.color }"
              @click="(e) => toggleColorPicker(e, index)"
            />
          </div>
        </div>
        <div class="row-actions">
          <Button
            icon="far fa-trash-alt"
            class="p-button-text p-button-danger p-button-sm"
            :disabled="prizes.length <= 2"
            @click="removePrize(index)"
          />
        </div>
      </div>
    </div>

    <div class="prizes-footer">
      <div class="probability-total" :class="{ 'is-valid': totalProbability === 100, 'is-invalid': totalProbability !== 100 }">
        <span>{{ t('templateBuilder.games.totalProbability') }}</span>
        <span class="total-value">{{ totalProbability }}%</span>
      </div>
      <Button
        :label="t('templateBuilder.games.addPrize')"
        icon="far fa-plus"
        class="p-button-secondary p-button-sm"
        :disabled="totalProbability >= 100"
        @click="addPrize"
      />
    </div>
    <div v-if="totalProbability < 100" class="probability-warning">
      <i class="fas fa-exclamation-triangle" />
      <span>{{ t('templateBuilder.games.probabilityWarning') }}</span>
    </div>

    <!-- Shared color picker overlay -->
    <OverlayPanel ref="colorPickerOverlay">
      <CustomColorPicker
        v-if="activeColorIndex !== null"
        :color="prizes[activeColorIndex]?.color || '#FF6B6B'"
        color-format="HEX"
        :show-alpha="false"
        :show-gradient="false"
        title="Color Picker"
        @color-changed="onColorChange"
      />
    </OverlayPanel>

    <!-- File Manager Modal -->
    <FileManager
      v-if="displayImageModal"
      :display-dialog="displayImageModal"
      extensions="image/*"
      @on-close-dialog="displayImageModal = false"
      @on-choose-file="onChooseImage"
    />
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  PropType,
  ref,
  computed,
  watch,
  onMounted,
  nextTick,
} from 'vue';
import { useI18n } from 'vue-i18n';
import InputText from 'primevue/inputtext';
import InputNumber from 'primevue/inputnumber';
import Button from 'primevue/button';
import OverlayPanel from 'primevue/overlaypanel';
import CustomColorPicker from '@/components/fields/partials/CustomColorPicker.vue';
import FileManager from '@/components/file-manager/FileManager.vue';
import {
  FieldConfig,
  ParserFieldObject,
  Property,
  File,
} from '@/types';
import {
  replaceTranslationInState,
  addTranslationsToState,
  TemplateEditorState as state,
  syncGameFunnelSteps,
} from '@/composables/template-editor/TemplateEditor';
import {
  createTranslationIdentifier,
  removeTranslationInState,
} from '@/components/template-builder/utils/translate';
import {
  Lang,
  UserState,
  getShopDefaultLang,
} from '@/composables/User';
import { getTemplateIframeDocument } from '@/components/template-builder/utils/helpers';

interface GamePrize {
  label: string;
  probability: number;
  color?: string;
  image?: string;
  translationKey?: string;
  templateId?: number;
  stock?: number | null;
}

export default defineComponent({
  name: 'GamePrizesEditor',

  components: {
    InputText,
    InputNumber,
    Button,
    OverlayPanel,
    CustomColorPicker,
    FileManager,
  },

  props: {
    configs: {
      type: Object as PropType<FieldConfig>,
      required: true,
    },

    parserValues: {
      type: Object as PropType<ParserFieldObject>,
      required: true,
    },
  },

  emits: ['on-change-properties'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const property = ref<Property>(props.parserValues.properties[0]);
    const prizes = ref<GamePrize[]>([]);
    const colorPickerOverlay = ref();
    const activeColorIndex = ref<number | null>(null);
    const displayImageModal = ref(false);
    const activeImageIndex = ref<number | null>(null);

    const showColorPicker = computed(() => props.configs.options?.showColorPicker ?? false);
    let internalChange = false;

    const totalProbability = computed(() => {
      return prizes.value.reduce((sum, prize) => sum + (prize.probability || 0), 0);
    });

    /**
     * Stock limits are edited in the separate "Stock" accordion (GameStockEditor), which writes
     * them straight into the iframe attribute. This editor keeps its own copy of the prizes and
     * rewrites the whole attribute on every change, so before each write the stock of every prize
     * is re-read from the iframe (source of truth). Without this, editing a label after setting a
     * stock silently reset every stock to null (SC-3828).
     */
    const syncStockFromIframe = () => {
      try {
        const template = getTemplateIframeDocument();
        if (!template) return;
        const element = template.querySelector(props.parserValues.selector);
        if (!element) return;
        const iframePrizes = JSON.parse(element.getAttribute('data-prizes') || '[]');
        if (!Array.isArray(iframePrizes)) return;
        prizes.value.forEach((_prize: GamePrize, index: number) => {
          const stock = Number(iframePrizes[index]?.stock);
          prizes.value[index].stock = stock > 0 ? stock : null;
        });
      } catch (e) {
        // Unreadable attribute: keep the local copy
      }
    };

    const parseInitialValue = () => {
      try {
        const value = property.value.value;
        if (value && typeof value === 'string') {
          const parsed = JSON.parse(value);
          if (Array.isArray(parsed)) {
            prizes.value = parsed.map((item: any) => {
              let templateId: number | undefined;
              if (item.templateId) {
                try {
                  // templateId is base64 encoded in data-prizes
                  templateId = Number(atob(item.templateId));
                } catch {
                  // Fallback to direct number if not base64
                  templateId = Number(item.templateId);
                }
                if (Number.isNaN(templateId) || templateId <= 0) {
                  templateId = undefined;
                }
              }
              return {
                label: item.label || '',
                probability: item.probability || 0,
                color: item.color || '#FF6B6B',
                image: item.image || '',
                translationKey: item.translationKey || '',
                ...(templateId ? { templateId } : {}),
                stock: (item.stock != null && Number(item.stock) > 0) ? Number(item.stock) : null,
              };
            });

            // Sync templateId and translationKey from iframe (source of truth)
            try {
              const template = getTemplateIframeDocument();
              if (template) {
                const element = template.querySelector(props.parserValues.selector);
                if (element) {
                  const iframePrizesStr = element.getAttribute('data-prizes');
                  if (iframePrizesStr) {
                    const iframePrizes = JSON.parse(iframePrizesStr);
                    prizes.value.forEach((localPrize: GamePrize, index: number) => {
                      if (iframePrizes[index]?.translationKey && !localPrize.translationKey) {
                        localPrize.translationKey = iframePrizes[index].translationKey;
                      }
                      if (iframePrizes[index]?.templateId && !localPrize.templateId) {
                        try {
                          localPrize.templateId = Number(atob(iframePrizes[index].templateId));
                        } catch (e) {
                          localPrize.templateId = Number(iframePrizes[index].templateId);
                        }
                      }
                    });
                  }
                }
              }
            } catch (e) { /* ignore iframe sync errors */ }

            // Stock limits are owned by the "Stock" accordion: always take them from the iframe
            syncStockFromIframe();

            return;
          }
        }
      } catch (e) {
        console.warn('[GamePrizesEditor] Failed to parse initial value:', e);
      }

      // Default prizes if parsing failed
      prizes.value = [
        { label: '10% de réduction', probability: 30, color: '#FF6B6B' },
        { label: '20% de réduction', probability: 25, color: '#4ECDC4' },
        { label: 'Livraison gratuite', probability: 20, color: '#45B7D1' },
        { label: 'Perdu', probability: 25, color: '#DDA0DD' },
      ];
    };

    const emitChange = () => {
      const jsonValue = JSON.stringify(prizes.value);
      internalChange = true;
      property.value.value = jsonValue;
      emit('on-change-properties', {
        selector: props.parserValues.selector,
        properties: [property.value],
      });
      nextTick(() => { internalChange = false; });
    };

    const updateIframeDataPrizes = () => {
      try {
        const template = getTemplateIframeDocument();
        if (!template) return;
        const element = template.querySelector(props.parserValues.selector);
        if (!element) return;

        // Sync translationKey from iframe state (added by translation system)
        const iframePrizesStr = element.getAttribute('data-prizes');
        if (iframePrizesStr) {
          try {
            const iframePrizes = JSON.parse(iframePrizesStr);
            prizes.value.forEach((localPrize: GamePrize, index: number) => {
              if (iframePrizes[index]?.translationKey && !localPrize.translationKey) {
                localPrize.translationKey = iframePrizes[index].translationKey;
              }
              if (iframePrizes[index]?.templateId && !localPrize.templateId) {
                try {
                  localPrize.templateId = Number(atob(iframePrizes[index].templateId));
                } catch (e) {
                  localPrize.templateId = Number(iframePrizes[index].templateId);
                }
              }
            });
          } catch (e) { /* ignore parse errors */ }
        }

        const prizesForDom = prizes.value.map((p: GamePrize) => ({
          ...p,
          templateId: p.templateId ? btoa(String(p.templateId)) : undefined,
        }));
        element.setAttribute('data-prizes', JSON.stringify(prizesForDom));

        // Refresh wheel via iframe's SPMGames global (handles replaced DOM elements)
        const iframeWindow = (document.querySelector('iframe.template-wrapper') as HTMLIFrameElement)?.contentWindow as any;
        if (iframeWindow?.shopimind?.SPMGames?.refreshWheelByElement) {
          iframeWindow.shopimind.SPMGames.refreshWheelByElement(element);
        } else if ((element as any)._spmGameWheel) {
          (element as any)._spmGameWheel.refresh();
        }
      } catch (e) {
        // Iframe not available
      }
    };

    const onPrizeChange = () => {
      syncStockFromIframe();
      // Sync prize labels to translation state
      const defaultLang = getShopDefaultLang();
      prizes.value.forEach((prize) => {
        if (prize.translationKey) {
          replaceTranslationInState(prize.translationKey, defaultLang, prize.label);
        }
      });
      emitChange();
      // Directly update iframe for immediate visual feedback
      updateIframeDataPrizes();
      // Sync funnel steps to reflect label changes
      syncGameFunnelSteps();
    };

    const onProbabilityChange = (index: number, newValue: number | null) => {
      if (newValue === null) {
        prizes.value[index].probability = 0;
        onPrizeChange();
        return;
      }

      // Calculate total of other prizes (excluding current one)
      const otherTotal = prizes.value.reduce((sum, prize, i) => {
        return i === index ? sum : sum + (prize.probability || 0);
      }, 0);

      // If new value would exceed 100%, cap it
      const maxAllowed = 100 - otherTotal;
      if (newValue > maxAllowed) {
        prizes.value[index].probability = Math.max(0, maxAllowed);
      } else {
        prizes.value[index].probability = newValue;
      }

      onPrizeChange();
    };

    const toggleColorPicker = (event: MouseEvent, index: number) => {
      activeColorIndex.value = index;
      colorPickerOverlay.value.toggle(event);
    };

    const onColorChange = (color: string) => {
      if (activeColorIndex.value !== null && prizes.value[activeColorIndex.value]) {
        syncStockFromIframe();
        prizes.value[activeColorIndex.value].color = color;
        emitChange();
        updateIframeDataPrizes();
      }
    };

    const openImagePicker = (index: number) => {
      activeImageIndex.value = index;
      displayImageModal.value = true;
    };

    const onChooseImage = (image: File) => {
      if (activeImageIndex.value !== null && prizes.value[activeImageIndex.value]) {
        syncStockFromIframe();
        prizes.value[activeImageIndex.value].image = image.url;
        emitChange();
        updateIframeDataPrizes();
      }
      displayImageModal.value = false;
      activeImageIndex.value = null;
    };

    const removeImage = (index: number) => {
      if (prizes.value[index]) {
        syncStockFromIframe();
        prizes.value[index].image = '';
        emitChange();
        updateIframeDataPrizes();
      }
    };

    const addPrize = () => {
      syncStockFromIframe();
      const colors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#DDA0DD', '#FFEAA7', '#74B9FF', '#FD79A8'];
      const colorIndex = prizes.value.length % colors.length;

      // Create a translation entry for the new prize
      const key = createTranslationIdentifier();
      const normalizedKey = `${key}.data-spm-text`;
      const defaultLabel = 'Nouveau prix';

      const translations = UserState.activeShop?.langs.map((language: Lang) => ({
        section: state.template?.activeSection ?? 0,
        language: language.id,
        fieldType: 'data-spm-text',
        key: normalizedKey,
        value: defaultLabel,
        callback: (value: string) => value,
      })) ?? [];

      addTranslationsToState(translations);

      prizes.value.push({
        label: defaultLabel,
        probability: 0,
        color: colors[colorIndex],
        translationKey: normalizedKey,
      });
      emitChange();
      updateIframeDataPrizes();
      syncGameFunnelSteps();
    };

    const removePrize = (index: number) => {
      if (prizes.value.length > 2) {
        // Read the stocks before the splice so each stock follows its prize, not its position
        syncStockFromIframe();
        const prize = prizes.value[index];
        // Remove translation entries for this prize
        if (prize.translationKey) {
          removeTranslationInState(prize.translationKey);
        }
        prizes.value.splice(index, 1);
        emitChange();
        updateIframeDataPrizes();
        syncGameFunnelSteps();
      }
    };

    onMounted(() => {
      parseInitialValue();
      // Sync funnel steps on initial load
      nextTick(() => syncGameFunnelSteps());
    });

    watch(() => props.parserValues.properties[0]?.value, () => {
      if (!internalChange) {
        parseInitialValue();
        nextTick(() => syncGameFunnelSteps());
      }
    }, { deep: true });

    return {
      t,
      prizes,
      showColorPicker,
      totalProbability,
      colorPickerOverlay,
      activeColorIndex,
      displayImageModal,
      onPrizeChange,
      onProbabilityChange,
      toggleColorPicker,
      onColorChange,
      openImagePicker,
      onChooseImage,
      removeImage,
      addPrize,
      removePrize,
    };
  },
});
</script>

<style lang="scss" scoped>
@import '@/assets/scss/utilities/_variables.scss';

.game-prizes-editor {
  width: 100%;

  .prizes-header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 0.25rem;
    font-size: 0.7rem;
    color: $mid-grey;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    font-weight: 600;
    border-bottom: 1px solid $solitude;
    margin-bottom: 0.25rem;

    .header-drag {
      width: 20px;
      flex-shrink: 0;
    }

    .header-label {
      flex: 1;
      min-width: 0;
    }

    .header-probability {
      width: 100px;
      min-width: 100px;
      flex-shrink: 0;
      flex-grow: 0;
      text-align: center;
    }

    .header-color {
      width: 36px;
      flex-shrink: 0;
      text-align: center;
    }

    .header-image {
      width: 36px;
      flex-shrink: 0;
      text-align: center;
    }

    .header-actions {
      width: 32px;
      flex-shrink: 0;
    }
  }

  .prizes-list {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    margin-bottom: 0.75rem;
  }

  .prize-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.375rem 0.25rem;
    background: $white;
    border: 1px solid $solitude;
    border-radius: 4px;
    transition: border-color 0.2s ease;

    &:hover {
      border-color: $brand-color-primary;
    }

    .row-drag {
      width: 20px;
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;

      .drag-handle {
        color: $mid-grey;
        cursor: grab;
        font-size: 0.75rem;

        &:active {
          cursor: grabbing;
        }
      }
    }

    .row-label {
      flex: 1;
      min-width: 0;
    }

    .row-probability {
      width: 100px;
      min-width: 100px;
      flex-shrink: 0;
      flex-grow: 0;

      :deep(.p-inputnumber) {
        width: 100%;
        min-width: 100%;

        .p-inputnumber-input {
          width: 100%;
          text-align: center;
          padding: 0.25rem 0.5rem;
          font-size: 0.875rem;
        }
      }
    }

    .row-color {
      width: 36px;
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;

      .color-picker-wrapper {
        .color-button {
          width: 28px;
          height: 28px;
          min-width: 28px;
          padding: 0;
          border: 2px solid $solitude;
          border-radius: 4px;
          cursor: pointer;
          transition: border-color 0.2s ease;

          &:hover {
            border-color: $brand-color-primary;
          }
        }
      }
    }

    .row-image {
      width: 36px;
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;

      .image-picker-wrapper {
        .image-button {
          width: 28px;
          height: 28px;
          min-width: 28px;
          padding: 0;
          border: 2px solid $solitude;
          border-radius: 4px;
          cursor: pointer;
          transition: border-color 0.2s ease;
          background: $white;
          color: $mid-grey;

          &:hover {
            border-color: $brand-color-primary;
            color: $brand-color-primary;
          }

          &.image-button-empty {
            :deep(.p-button-icon) {
              font-size: 0.875rem;
            }
          }
        }

        .image-thumbnail-wrapper {
          position: relative;
          width: 28px;
          height: 28px;
          border: 2px solid $solitude;
          border-radius: 4px;
          overflow: hidden;
          cursor: pointer;
          transition: border-color 0.2s ease;

          &:hover {
            border-color: $brand-color-primary;

            .image-remove-btn {
              opacity: 1;
            }
          }

          .image-thumbnail {
            width: 100%;
            height: 100%;
            object-fit: cover;
          }

          .image-remove-btn {
            position: absolute;
            top: -4px;
            right: -4px;
            width: 16px;
            height: 16px;
            min-width: 16px;
            padding: 0;
            border-radius: 50%;
            background: $brand-color-primary;
            color: $white;
            opacity: 0;
            transition: opacity 0.2s ease;

            :deep(.p-button-icon) {
              font-size: 0.625rem;
            }
          }
        }
      }
    }

    .row-actions {
      width: 32px;
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;

      :deep(.p-button) {
        width: 28px;
        height: 28px;
        padding: 0;
      }
    }
  }

  .prizes-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-top: 0.5rem;
    border-top: 1px solid $solitude;
  }

  .probability-total {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.875rem;

    .total-value {
      font-weight: 600;
      padding: 0.25rem 0.5rem;
      border-radius: 4px;
    }

    &.is-valid .total-value {
      background: rgba(76, 175, 80, 0.15);
      color: #2e7d32;
    }

    &.is-invalid .total-value {
      background: rgba(244, 67, 54, 0.15);
      color: #c62828;
    }
  }

  .probability-warning {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-top: 0.5rem;
    padding: 0.5rem 0.75rem;
    background: rgba(255, 152, 0, 0.1);
    border: 1px solid rgba(255, 152, 0, 0.3);
    border-radius: 4px;
    color: #e65100;
    font-size: 0.8rem;

    i {
      font-size: 0.875rem;
    }
  }
}
</style>
